import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, OfflineError } from '../api/http';
import {
  createServer,
  downloadingIds,
  fetchServers,
  formatUptime,
  forceStandardFlightEverywhere,
  installAddon,
  serverAction,
  type AddonState,
  type ServerRequest,
  type FlightModelResult,
  type ServersResponse,
} from '../api/servers';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { ModMosaic } from './ModMosaic';
import { ServerStateTag } from './ServerStateTag';
import { OverflowMenu } from './OverflowMenu';
import { Planned } from './Planned';
import { ServerForm } from './ServerForm';
import { missionOn, originOf, runningFirst, serverWorld } from './serverMods';
import { PageLoading } from './PageStates';
import { Shell } from './Shell';

function errorText(err: unknown): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  if (!(err instanceof ApiError)) return ca.errors.unknown;
  // Mods a new server was asked to carry that the panel had not downloaded: it
  // is downloading them, and the names say which.
  if (err.code === 'mods_downloading') {
    const mods = (err.body as { mods?: string[] } | null)?.mods ?? [];
    return ca.serversPage.modsDownloading(mods);
  }
  const base = ca.errors[err.code] ?? ca.errors.unknown;
  // A panel_declined carries the guard's own reason ("Server 'X' belongs to
  // the operator and is not linkable"): the one thing an admin needs to act
  // on, thrown away until ServersController answered every refusal as a
  // generic panel_unreachable.
  return err.detail ? `${base} ${err.detail}` : base;
}

type Filter = 'all' | 'managed' | 'linked' | 'stopped';

/** What the addon chip says and whether it warns; null when the backend has no addon state yet. */
function addonChip(addon: AddonState | undefined): { text: string; tone: 'ok' | 'warn' } | null {
  if (!addon) return null;
  const s = ca.serversPage;
  if (!addon.installed) return { text: s.addonMissing, tone: 'warn' };
  if (addon.bundled && addon.bundledVersion && addon.installedVersion !== addon.bundledVersion)
    return { text: s.addonOutdated(addon.bundledVersion), tone: 'warn' };
  return { text: s.addonInstalled(addon.installedVersion), tone: 'ok' };
}

/**
 * The servers page (UI doc 5.12): every server the panel has as a card that
 * opens the server's own page, with the budget, the panel and the addon state
 * in the header, filters, and creation from the drawer.
 *
 * A card shows the server's mods as a regular grid of tiles (`ModMosaic`; a
 * vanilla server shows Arma 3 alone), its state is a tag, the running ones
 * come first, only a running server names a mission, and it offers the one
 * action its state calls for. What applies to every server at
 * once sits in the header's menu.
 */
export function Servers() {
  const { me } = useAuth();
  const [data, setData] = useState<ServersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<number[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [addonBusy, setAddonBusy] = useState(false);
  const [addonNotice, setAddonNotice] = useState<string | null>(null);
  const [flightBusy, setFlightBusy] = useState(false);
  const [flightResults, setFlightResults] = useState<FlightModelResult[] | null>(null);
  const isAdmin = me.roles.includes('ADMIN');
  const s = ca.serversPage;

  const load = useCallback(async () => {
    try {
      setData(await fetchServers());
      setError(null);
    } catch (err) {
      setData(null);
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (me.authenticated) void load();
  }, [me.authenticated, load]);

  async function act(id: number, action: 'start' | 'stop') {
    setBusy(id);
    try {
      await serverAction(id, action);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function submitForm(input: ServerRequest) {
    setFormError(null);
    setDownloading([]);
    try {
      await createServer(input);
      setDrawer(false);
      await load();
    } catch (err) {
      setFormError(errorText(err));
      setDownloading(downloadingIds(err));
    }
  }

  async function onInstallAddon() {
    setAddonBusy(true);
    setAddonNotice(null);
    try {
      await installAddon();
      setAddonNotice(s.addonDone);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setAddonBusy(false);
    }
  }

  async function onForceStandardFlight() {
    setFlightBusy(true);
    setFlightResults(null);
    try {
      setFlightResults(await forceStandardFlightEverywhere());
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setFlightBusy(false);
    }
  }

  const budgetLeft = data ? data.budget.max - data.budget.used : 0;

  const filtered = useMemo(() => {
    const servers = data?.servers ?? [];
    const q = query.trim().toLowerCase();
    const kept = servers.filter((sv) => {
      if (filter === 'managed' && sv.access !== 'MANAGED') return false;
      if (filter === 'linked' && sv.access !== 'LINKED') return false;
      if (filter === 'stopped' && sv.status.alive) return false;
      if (!q) return true;
      return (
        sv.name.toLowerCase().includes(q) ||
        (sv.missionTemplate ?? '').toLowerCase().includes(q) ||
        (sv.missionTitle ?? '').toLowerCase().includes(q) ||
        String(sv.port).includes(q)
      );
    });
    return runningFirst(kept);
  }, [data, filter, query]);

  const tabs: { key: Filter; label: string }[] = [
    { key: 'all', label: s.filterAll },
    { key: 'managed', label: s.filterManaged },
    { key: 'linked', label: s.filterLinked },
    { key: 'stopped', label: s.filterStopped },
  ];

  const addon = addonChip(data?.addon);
  const addonNeedsInstall =
    !!data?.addon && data.addon.bundled && (!data.addon.installed || addon?.tone === 'warn');

  return (
    <Shell
      title={ca.servers}
      chips={
        data && (
          <>
            <span className="chip">
              <i className="bi bi-hdd-stack" aria-hidden="true" />{' '}
              {ca.budgetLine(data.budget.used, data.budget.max)}
            </span>
            {data.panelReachable ? (
              <span className="chip ok">
                <span className="dot ok" /> {ca.panelConnected(data.lastSuccessSecondsAgo)}
              </span>
            ) : (
              <span className="chip bad">
                <span className="dot off" /> {s.panelDown(data.lastFailure)}
              </span>
            )}
            {addon && (
              <span className={`chip ${addon.tone}`}>
                <i className="bi bi-puzzle" aria-hidden="true" /> {addon.text}
              </span>
            )}
            {isAdmin && addonNeedsInstall && (
              <button
                type="button"
                className="small"
                disabled={addonBusy}
                onClick={() => void onInstallAddon()}
              >
                <i className="bi bi-download" aria-hidden="true" />
                {addonBusy ? s.addonInstalling : s.addonInstall}
              </button>
            )}
          </>
        )
      }
      actions={
        <>
          <button type="button" className="ghost" onClick={() => void load()}>
            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            {ca.refresh}
          </button>
          {isAdmin && data && data.servers.length > 0 && (
            <OverflowMenu label={s.actionsAll}>
              <button
                type="button"
                className="ghost"
                disabled={flightBusy}
                onClick={() => void onForceStandardFlight()}
              >
                <i className="bi bi-airplane" aria-hidden="true" />
                {flightBusy ? s.flightForcing : s.flightForceAll}
              </button>
            </OverflowMenu>
          )}
          <button
            type="button"
            className="primary"
            disabled={!data}
            onClick={() => {
              setFormError(null);
              setDrawer(true);
            }}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" />
            {ca.newServer}
          </button>
        </>
      }
    >
      {!data && !error && me.authenticated && <PageLoading />}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {addonNotice && <p className="notice ok">{addonNotice}</p>}
      {flightResults && (
        <section className="notice" aria-label={s.flightResultTitle}>
          <p>
            <strong>{s.flightResultTitle}</strong> · {s.flightResultNote}
          </p>
          <ul>
            {flightResults.map((r) => (
              <li key={r.id}>
                {r.name || `#${r.id}`}: {s.flightOutcome[r.outcome]}
                {r.outcome === 'failed' && r.detail ? ` · ${r.detail}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
      {data && data.servers.length > 0 && (
        <div className="toolbar">
          {tabs.map((t) => (
            <button
              type="button"
              key={t.key}
              className={`chip toggle ${filter === t.key ? 'on' : ''}`}
              aria-pressed={filter === t.key}
              onClick={() => setFilter(t.key)}
            >
              {t.label}
            </button>
          ))}
          <span className="grow" />
          <input
            className="control"
            type="search"
            aria-label={s.search}
            placeholder={s.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      <div className="split">
        <div className="cards">
          {data && data.servers.length === 0 && (
            <div className="empty">
              <i className="bi bi-hdd-network" aria-hidden="true" />
              <p>{ca.noServers}</p>
            </div>
          )}
          {data && data.servers.length > 0 && filtered.length === 0 && (
            <div className="empty">
              <i className="bi bi-search" aria-hidden="true" />
              <p>{s.empty}</p>
              <p className="small">{s.emptyHint}</p>
            </div>
          )}
          {filtered.length > 0 && (
            <section className="server-grid">
              {filtered.map((sv) => {
                const alive = sv.status.alive;
                const origin = originOf(sv.access);
                const world = serverWorld(sv);
                // The panel keeps the mission a server will start with whether or
                // not it runs: only a running server has a mission on.
                const mission = missionOn(sv);
                const canStart = sv.actions.includes('start') && !alive;
                const canStop = sv.actions.includes('stop') && alive;
                return (
                  <article
                    className={`card server-card ${alive ? 'on' : 'off'}`}
                    key={sv.id}
                    aria-label={sv.name}
                  >
                    <header className="server-head">
                      <h2 className="server-name">
                        <Link to={`/servers/${sv.id}`}>{sv.name}</Link>
                      </h2>
                      <ServerStateTag alive={alive} uptime={formatUptime(sv.status.startedAt)} />
                      <span className="server-origin muted small" title={ca.accessHint[sv.access]}>
                        {origin.label}
                        {origin.note && <> · {origin.note}</>}
                      </span>
                    </header>
                    <Link
                      className="server-mods"
                      to={`/servers/${sv.id}`}
                      aria-hidden
                      tabIndex={-1}
                    >
                      <ModMosaic mods={sv.activeMods} />
                    </Link>
                    {alive && (
                      <p className="server-mission">
                        <i className="bi bi-flag" aria-hidden="true" />
                        {mission ? (
                          <span className="what" title={sv.missionTemplate ?? undefined}>
                            {mission}
                          </span>
                        ) : (
                          <span className="what muted">{s.noMission}</span>
                        )}
                        {world && <span className="where muted">{world}</span>}
                      </p>
                    )}
                    <div className="row chips">
                      <span className="chip" title={ca.players}>
                        <i className="bi bi-people" aria-hidden="true" />{' '}
                        {s.playersChip(sv.status.playersOnline, sv.maxPlayers)}
                      </span>
                      <span className="chip">
                        <i className="bi bi-box-seam" aria-hidden="true" />{' '}
                        {s.modsCount(sv.activeMods.length)}
                      </span>
                      <span className="chip">{s.portChip(sv.port)}</span>
                      {sv.opsMod && (
                        <span className="chip ok" title={s.opsModHint}>
                          <i className="bi bi-puzzle" aria-hidden="true" /> {s.opsMod}
                        </span>
                      )}
                    </div>
                    <div className="row actions">
                      {canStart && (
                        <button
                          type="button"
                          className="primary small"
                          disabled={busy === sv.id}
                          onClick={() => void act(sv.id, 'start')}
                        >
                          <i className="bi bi-play-fill" aria-hidden="true" />
                          {ca.start}
                        </button>
                      )}
                      {canStop && (
                        <button
                          type="button"
                          className="small"
                          disabled={busy === sv.id}
                          onClick={() => void act(sv.id, 'stop')}
                        >
                          <i className="bi bi-stop-fill" aria-hidden="true" />
                          {ca.stop}
                        </button>
                      )}
                      <span className="grow" />
                      <Link className="button small ghost" to={`/servers/${sv.id}`}>
                        {s.open} <i className="bi bi-chevron-right" aria-hidden="true" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </section>
          )}
        </div>
        {drawer && (
          <ServerForm
            key="new"
            server={null}
            budgetLeft={budgetLeft}
            onSubmit={submitForm}
            onCancel={() => setDrawer(false)}
            error={formError}
            downloading={downloading}
          />
        )}
      </div>
      <Planned ids={['servers-mod-fingerprint', 'servers-probe-card']} />
    </Shell>
  );
}
